import { FC, useState } from 'react'

import _ from 'lodash'
import { styled } from 'styled-components'

import { MiniTable, Card, Json, LoadingContainer } from 'peach/components'
import { Page, Switch } from 'core/components'
import { humanizeKey } from 'peach/helpers'

import { R } from 'core/helpers'
import variables from 'core/theme/variables'
import { useCompanyGetAll, useUserPermissions_get } from 'core/types'
const join = (...args) => _.map(_.compact(args), humanizeKey).join(' / ')

const companyRow = ({ company, disabled }) => {
  const {
    id,
    agentAuthType,
    agentAuthValueType,
    borrowerAuthType,
    borrowerAuthValueType,
    config,
    timezone,
    name,
    brandName,
    legalName,
    type,
  } = company

  // This used to be color = config?.brandAssets?.colors?.primary
  // but sometimes when a company's primary color is too dark, the name
  // text of the company in the companies table gets hard to read
  // when the theme is set to dark mode.
  //
  // For now we're using the app's primary blue color.
  const color = variables.colorBluePrimary

  const processedByPeach = config?.payment?.processedByPeach

  const details = {
    type,
    processedByPeach,
    timezone,
  }

  const auth = {
    agentAuth: join(agentAuthValueType, agentAuthType),
    borrowerAuth: join(borrowerAuthValueType, borrowerAuthType),
  }

  const names = {
    name,
    brandName,
    legalName,
  }

  return (
    <MiniTable.Row key={id}>
      <Card color={color} disabled={disabled} model={company} />
      <Json.List compact humanizeKeys json={details} limit={3} />
      <Json.List compact humanizeKeys json={auth} limit={3} />
      <Json.List compact humanizeKeys json={names} limit={3} />
    </MiniTable.Row>
  )
}

const Companies: FC = () => {
  const [excludeInactive, setExcludeInactive] = useState(true)

  const {
    data: companies,
    isLoading: isLoadingCompanies,
    error: isErrorCompanies,
  } = useCompanyGetAll({
    query: { excludeInactive, limit: 100 },
  })

  const {
    data: permissions,
    isLoading: isLoadingPermissions,
    error: isErrorPermissions,
  } = useUserPermissions_get({})

  const rows = R.pipe(
    companies ?? [],
    R.map((company) => ({
      company,
      disabled: !_.includes(permissions?.[company.id!], 'company:read'),
    })),
    R.sortBy((company) => company.disabled),
    R.map(companyRow),
  )

  return (
    <LoadingContainer
      error={isErrorCompanies ?? isErrorPermissions}
      loading={isLoadingCompanies || isLoadingPermissions}
    >
      <Page variant='full'>
        <SwitchContainer>
          <Switch
            checked={excludeInactive}
            id='exclude-inactive'
            label='Exclude Inactive'
            name='excludeInactive'
            onCheckedChange={(checked) => setExcludeInactive(checked)}
          />
        </SwitchContainer>
        <MiniTable columns={['Company', 'Details', 'Auth', 'Names']} fixed>
          {rows}
        </MiniTable>
      </Page>
    </LoadingContainer>
  )
}

export default Companies

const SwitchContainer = styled.div`
  margin-bottom: 12px;
`
